import React from 'react';
import moment from 'moment';
import IntervalRenderer from 'flow/components/custom-components/interval-renderer';
import useAppContext from 'flow/hooks/useAppContext';
import RedirectPresentationWidget from 'flow/components/communication-canvas/RedirectPresentationWidget';
import { getPrettyDuration, getRedirectNumber } from 'flow/utils/call-utils';
import { getCallItemContactTitle } from 'flow/utils/contact-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import CoListeningContactItem from './CoListeningContactItem';
import IconButton from '../ui-components/IconButton';
import Icon from '../ui-components/Icon';
import { twMerge } from 'tailwind-merge';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { useAppSelector } from 'flow/hooks/useRedux';

interface Props {
    subjectContact: TypedMap<ContactFull>;
    subjectNumber: string;
    remotePartyOfSubjectContact: TypedMap<ContactFull>;
    remotePartyOfSubjectNumber: string;
    startTime: Date;
    ownCountryCode: string;
    //TODO: #typeFix - WEB-14418 - Type this when we start working with Remote calls https://telavox.atlassian.net/browse/WWEB-14775
    activeCallOfSubject: any;
    onHangup: () => void;
}

const CoListeningSupervisorSmall = ({
    subjectContact,
    subjectNumber,
    remotePartyOfSubjectContact,
    remotePartyOfSubjectNumber,
    startTime,
    ownCountryCode,
    activeCallOfSubject,
    onHangup,
}: Props) => {
    const { i18next } = useAppContext();

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);
    const agentName = getCallItemContactTitle(
        subjectContact,
        null,
        subjectNumber,
        ownCountryCode,
        i18next,
    );
    const customerName = getCallItemContactTitle(
        remotePartyOfSubjectContact,
        null,
        remotePartyOfSubjectNumber,
        ownCountryCode,
        i18next,
    );

    const showRoutingItem =
        activeCallOfSubject &&
        (activeCallOfSubject.get('queue') ||
            getRedirectNumber(activeCallOfSubject));

    return (
        <div className="my-1 flex w-full items-center rounded-sm bg-gray-800 px-1 text-xs">
            <div
                className={twMerge(
                    'items-center justify-between rounded-sm p-2',
                    isSinglePaneMode
                        ? 'block flex-auto space-y-2'
                        : 'flex flex-1 gap-2',
                )}
            >
                <div className="bg-yellow flex h-9 items-center justify-center truncate rounded-sm px-2 font-bold text-gray-800">
                    {i18next.t('callcontrol.colistening.listening.counter')}
                    <IntervalRenderer interval={1000}>
                        {() =>
                            getPrettyDuration(
                                moment.duration(
                                    moment().diff(moment(startTime)),
                                ),
                            )
                        }
                    </IntervalRenderer>
                </div>
                {/* min-w-0, not overflow-hidden: the contact items truncate
                    themselves, and a clip here would cut the focus ring of
                    the stop button at the end. */}
                <div className="flex min-w-0 flex-1 items-center">
                    <CoListeningContactItem
                        contact={subjectContact}
                        contactName={agentName.text}
                        size="small"
                    />

                    {isSinglePaneMode ? (
                        <Icon
                            className="mx-2 text-white"
                            iconKey="phone_in_talk"
                        />
                    ) : (
                        <span
                            className={twMerge(
                                'm-2 whitespace-nowrap text-white',
                            )}
                        >
                            {i18next.t('callcontrol.colistening.talking.to')}
                        </span>
                    )}

                    <CoListeningContactItem
                        contact={remotePartyOfSubjectContact}
                        contactName={customerName.text}
                        size={'small'}
                    />
                    <div className="ms-auto flex ps-2">
                        <IconButton
                            onClick={onHangup}
                            className="hover:darken bg-red text-white"
                            data-test-id={'abort-button'}
                            tooltipLabel={i18next.t(
                                'callcontrol.colistening.stop.listening',
                            )}
                        >
                            <Icon iconKey="close" />
                        </IconButton>
                    </div>
                </div>
                {showRoutingItem && (
                    <div className="overflow-hidden ps-3">
                        <RedirectPresentationWidget
                            activeCall={activeCallOfSubject}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoListeningSupervisorSmall;
